import { createContext, useCallback, useContext } from "react";

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  buildPinBatchedQueries,
  type PinBatchedQuery,
} from "src/analytics/dashboard/dashboardFilterParams";
import { enqueueMetricsQuery } from "src/analytics/metrics/queryBatching";
import type {
  MetricsQueryRawResult,
  MetricsQueryRow,
} from "src/analytics/metrics/useMetricsQuery";
import { useCurrentWorkspace } from "src/api/queries";
import type { Pin } from "src/clients/analytics-dashboard";
import { type Environment, useEnvironment } from "src/hooks/useEnvironment";
import type { StrictDateRange } from "src/utils/timeWindow";
import { getLocalTimezone } from "src/utils/timezones";

// Wrapped so a missing slice still resolves with defined data: react-query
// rejects an undefined queryFn result.
type PinQueryResult = { result: MetricsQueryRawResult | undefined };

export const dashboardMetricsKeys = {
  all: ["dashboard-metrics"] as const,
  // The chart and its own query, so a sibling being pinned or unpinned cannot
  // invalidate it, and charts sharing a query keep the distinct keys
  // `useQueries` requires — the batcher still sends that query once.
  pin: (
    baseUrl: string | undefined,
    dataSource: string | undefined,
    pinId: string,
    query: PinBatchedQuery,
  ) =>
    [
      ...dashboardMetricsKeys.all,
      baseUrl,
      dataSource,
      pinId,
      JSON.stringify(query),
      // staleTime is Infinity, so a zone change must change the key.
      getLocalTimezone(),
    ] as const,
};

type PinMetricsQuery = {
  queryKey: ReturnType<typeof dashboardMetricsKeys.pin>;
  queryFn: () => Promise<PinQueryResult>;
  enabled: boolean;
};

type DashboardMetrics = {
  queryByPin: Record<string, PinMetricsQuery>;
  pinIds: string[];
  environment: Environment;
  timeWindow: StrictDateRange;
};

type PinMetrics = {
  rows: MetricsQueryRow[] | undefined;
  error: Error | null;
  isLoading: boolean;
};

const selectPinResult = (
  result: MetricsQueryRawResult | undefined,
): Pick<PinMetrics, "rows" | "error"> => {
  // Missing slice = contract violation; an error beats a misleading empty chart.
  if (!result) {
    return {
      rows: undefined,
      error: new Error("No result returned for this chart"),
    };
  }
  if (result.error !== undefined) {
    return { rows: undefined, error: new Error(result.error) };
  }
  const { columns, rows } = result;
  const mapped = (rows ?? []).map((row) =>
    Object.fromEntries((columns ?? []).map((col, i) => [col, row[i]])),
  );
  return { rows: mapped, error: null };
};

const DashboardMetricsContext = createContext<DashboardMetrics | null>(null);

// Per-chart `select` + structural sharing: a chart re-renders only when its
// own slice changes, not when a sibling's data arrives.
export const usePinMetrics = (pinId: string): PinMetrics => {
  const metrics = useContext(DashboardMetricsContext);
  if (!metrics) {
    throw new Error(
      "usePinMetrics must be used within a DashboardMetricsProvider",
    );
  }
  const pinQuery: PinMetricsQuery | undefined = metrics.queryByPin[pinId];
  const { data, error, isFetching } = useQuery({
    queryKey: pinQuery?.queryKey ?? [
      ...dashboardMetricsKeys.all,
      "unknown",
      pinId,
    ],
    queryFn:
      pinQuery?.queryFn ??
      (() => Promise.resolve<PinQueryResult>({ result: undefined })),
    enabled: pinQuery?.enabled ?? false,
    staleTime: Infinity,
    select: ({ result }) => selectPinResult(result),
  });
  return {
    rows: data?.rows,
    error: error ?? data?.error ?? null,
    isLoading: isFetching,
  };
};

const usePinQueries = (metrics: DashboardMetrics) =>
  useQueries({
    queries: metrics.pinIds.map((pinId) => ({
      ...metrics.queryByPin[pinId],
      staleTime: Infinity,
    })),
  });

// Resolves already-fetched rows for any pin in the batch without a hook call
// per pin, for callers that need data for a dynamic set of pins.
export const usePinRowsResolver = (): ((
  pinId: string,
) => MetricsQueryRow[] | undefined) => {
  const metrics = useContext(DashboardMetricsContext);
  if (!metrics) {
    throw new Error(
      "usePinRowsResolver must be used within a DashboardMetricsProvider",
    );
  }
  const results = usePinQueries(metrics);
  return useCallback(
    (pinId: string) => {
      const index = metrics.pinIds.indexOf(pinId);
      const data = index >= 0 ? results[index]?.data : undefined;
      if (!data) return undefined;
      const { rows, error } = selectPinResult(data.result);
      return error ? undefined : rows;
    },
    [metrics, results],
  );
};

export const useDashboardMetricsParams = (): {
  environment: Environment;
  timeWindow: StrictDateRange;
} => {
  const shared = useContext(DashboardMetricsContext);
  if (!shared) {
    throw new Error(
      "useDashboardMetricsParams must be used within a DashboardMetricsProvider",
    );
  }
  return { environment: shared.environment, timeWindow: shared.timeWindow };
};

type DashboardMetricsRefresh = {
  refresh: () => void;
  isRefreshing: boolean;
  lastUpdatedAt: number | undefined;
};

export const useDashboardMetricsRefresh = (): DashboardMetricsRefresh => {
  const metrics = useContext(DashboardMetricsContext);
  if (!metrics) {
    throw new Error(
      "useDashboardMetricsRefresh must be used within a DashboardMetricsProvider",
    );
  }
  const queryClient = useQueryClient();
  const results = usePinQueries(metrics);
  const enabledResults = results.filter(
    (_, index) => metrics.queryByPin[metrics.pinIds[index]].enabled,
  );
  // Gate on isPending, not dataUpdatedAt: waiting for every chart to settle
  // avoids a premature timestamp mid-load, while still surfacing the loaded
  // charts when a sibling fails (a failed chart keeps dataUpdatedAt at 0).
  const allChartsSettled =
    enabledResults.length > 0 &&
    enabledResults.every((result) => !result.isPending);
  const succeededUpdatedAts = enabledResults
    .map((result) => result.dataUpdatedAt)
    .filter((updatedAt) => updatedAt > 0);
  return {
    refresh: () =>
      void queryClient.invalidateQueries({
        queryKey: dashboardMetricsKeys.all,
      }),
    isRefreshing: results.some((result) => result.isFetching),
    // Oldest chart wins: the board is only as fresh as its stalest slice.
    lastUpdatedAt:
      allChartsSettled && succeededUpdatedAts.length > 0
        ? Math.min(...succeededUpdatedAts)
        : undefined,
  };
};

export const DashboardMetricsProvider: React.FC<{
  pins: Pin[];
  timeWindow: StrictDateRange;
  dataSource?: string;
  children: React.ReactNode;
}> = ({ pins, timeWindow, dataSource, children }) => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;
  const [environment] = useEnvironment();

  const queries = buildPinBatchedQueries(pins, environment, timeWindow);
  const queryByPin: Record<string, PinMetricsQuery> = Object.fromEntries(
    pins.map((pin) => {
      const query = queries[pin.id];
      return [
        pin.id,
        {
          queryKey: dashboardMetricsKeys.pin(
            baseUrl,
            dataSource,
            pin.id,
            query,
          ),
          queryFn: async () => {
            if (!baseUrl) throw new Error("Workspace not loaded");
            return {
              result: await enqueueMetricsQuery(
                baseUrl,
                dataSource,
                pin.id,
                query,
              ),
            };
          },
          enabled: Boolean(baseUrl),
        },
      ];
    }),
  );

  const metrics: DashboardMetrics = {
    queryByPin,
    pinIds: pins.map((pin) => pin.id),
    environment,
    timeWindow,
  };

  return (
    <DashboardMetricsContext value={metrics}>
      {children}
    </DashboardMetricsContext>
  );
};
